import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Skeleton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { NavLink, useNavigate } from "react-router-dom";
import CustomPersonIcon from "../../assets/sellerNavbarIcons/ProfileIcon_SellerNavbar.svg";
import arrowDown from "../../assets/sellerNavbarIcons/arrowDown.svg";
import CustomersIcon from "../../assets/sellerNavbarIcons/customers.svg";
import CatalogIcon from "../../assets/sellerNavbarIcons/catalog.svg";
import OrdersIcon from "../../assets/sellerNavbarIcons/orders.svg";
import ShipmentIcon from "../../assets/sellerNavbarIcons/shipment.svg";
import InventoryIcon from "../../assets/sellerNavbarIcons/inventory.svg";
import UserManagementIcon from "../../assets/sellerNavbarIcons/userManagement.svg";
import salesRepManagementIcon from "../../assets/sellerNavbarIcons/salesRepManagement.svg";
import RupeeIcon from "../../assets/sellerNavbarIcons/rupeeIcon.svg";
import LogoutIcon from "../../assets/sellerNavbarIcons/logout.svg";
import Analytics from "../../assets/sellerNavbarIcons/analytics.svg";
import { AuthApiService } from "../../service/authService";
import { useAppDispatch } from "../../redux/hooks";
import { reset } from "../../redux/features/slices/appSlice";
import { useRole } from "../../context/RoleContext";
import { routes } from "../../routes/routes";
import PreBookingIcon from "../../assets/sellerNavbarIcons/prebookings.svg";
import { useQueryClient } from "@tanstack/react-query";
import { logoutWithBackend } from "../../api/loginAPI";
import Receipt from "../../assets/sellerNavbarIcons/receipt.svg";
import FeedbackIcon from "../../assets/sellerNavbarIcons/feedback.svg";
import colors from "../../theme/Colours";

const NavContainer = styled(AppBar)(() => ({
  backgroundColor: "#fff",
  color: "#000",
  boxShadow: "20px",
}));

const NavItem = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  minHeight: "20px",
}));

const IconStyle = {
  width: "20px",
  height: "20px",
  opacity: 1,
};

type SellerNavbarProps = {
  logo?: string;
};

const logoStyle = {
  height: "30px",
  cursor: "pointer",
  margin: "11.5px 55px 11.5px 0px",
};

const SellerNavbar: React.FC<SellerNavbarProps> = ({ logo }) => {
  const orgId = localStorage.getItem("orgId");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const [userName, setUserName] = useState<string>(
    localStorage.getItem("Name") || ""
  );

  useEffect(() => {
    const updateUserName = () => {
      const storedName = localStorage.getItem("Name") || "";
      setUserName(storedName);
    };

    // Update username on component mount
    updateUserName();

    // Listen for storage changes (for cross-tab updates)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "Name") {
        updateUserName();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Also check periodically for localStorage changes (same tab updates)
    const interval = setInterval(updateUserName, 1000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);
  const { role } = useRole();
  const queryClient = useQueryClient();
  const NAV_ITEMS = [
    {
      label: "Dashboard",
      to: "dashboard",
      icon: Analytics,
      style: { width: "17px", height: "17px" },
    },
    {
      label: "Customers",
      to: "all-customers",
      icon: CustomersIcon,
    },
    { label: "Catalog", to: "products", icon: CatalogIcon },
    { label: "Orders", to: "orders", icon: OrdersIcon },
    {
      label: "Shipment",
      to: "shipment",
      icon: ShipmentIcon,
      style: { width: "15.66px", height: "17px" },
    },
    { label: "Inventory", to: "inventory", icon: InventoryIcon },
    {
      label: userName,
      icon: CustomPersonIcon,
      subMenu: [
        {
          label: "Profile Management",
          to: "profile-management",
          icon: CustomPersonIcon,
        },
        {
          label: "User Management",
          to: "user-management",
          icon: UserManagementIcon,
        },
        {
          label: "Sales Rep Management",
          to: "sales-rep-management",
          icon: salesRepManagementIcon,
        },
        {
          label: "Credit Management",
          to: "credit-management",
          icon: RupeeIcon,
          disabled: false,
        },
        {
          label: "Pre-Booking Orders",
          to: "pre-bookings",
          icon: PreBookingIcon,
        },
        { label: "Invoice History", to: "invoice-history", icon: Receipt },
        {
          label: "Feedback",
          action: "feedback",
          icon: FeedbackIcon,
        },
        {
          label: "Logout",
          action: "logout",
          icon: LogoutIcon,
        },
      ],
    },
  ];

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleMenuItemClick = (path: string) => {
    navigate(`${role}/${path}`);
    handleMenuClose();
  };

  const handleLogout = () => {
    logoutWithBackend()
      .catch((err) => console.error("Logout failed:", err))
      .finally(() => {
        AuthApiService.logout()
          .catch((err) => console.error("Logout failed:", err))
          .finally(() => {
            dispatch(reset());
            queryClient.clear();
            navigate(`/${routes.public.signIn}`, {
              replace: true,
              state: { fromApp: true },
            });
          });
      });
  };

  const handleFeedback = () => {
    window.open("https://tally.so/r/J9epXR", "_blank");
    handleMenuClose();
  };

  const activeLinkStyle = { textDecoration: "underline", color: "#000" };
  const linkStyle = { textDecoration: "none", color: "#000" };

  return (
    <NavContainer position="fixed">
      <Toolbar>
        {/* Logo Section */}
        <Box>
          <NavLink to={`/${role}/dashboard`}>
            {!logo ? (
              <Skeleton variant="rectangular" sx={logoStyle} />
            ) : (
              <img src={logo} alt="Logo" style={logoStyle} />
            )}
          </NavLink>
        </Box>

        {/* Navigation */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-evenly",
            alignItems: "center",
            flexGrow: 1,
          }}
        >
          {NAV_ITEMS.map(({ label, to, icon, style, subMenu }) =>
            subMenu ? (
              <Box key={label}>
                <IconButton
                  onClick={handleProfileMenuOpen}
                  color="inherit"
                  disableRipple
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <img
                    src={icon}
                    alt={label}
                    style={{ width: "16px", height: "16px" }}
                  />
                  <Typography
                    sx={{
                      paddingX: "12px",
                      fontSize: "14px",
                      display: "flex",
                      alignItems: "center",
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    {label}
                  </Typography>
                  <img src={arrowDown} />
                </IconButton>
                <Menu
                  data-testid="user-menu"
                  anchorEl={anchorEl}
                  open={isMenuOpen}
                  onClose={handleMenuClose}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  sx={{
                    transform: "translateX(30px)",
                    ".css-1toxriw-MuiList-root-MuiMenu-list": {
                      marginTop: "4px",
                      padding: "14px 7px",
                      outline: 0,
                      fontFamily: "Poppins, sans-serif",
                      borderRadius: "10px",
                      border: "1px solid #64748B",
                    },
                    ".MuiPaper-root": {
                      boxShadow: "none",
                    },
                    ".MuiMenuItem-root": {
                      padding: "8px 10px",
                      "&:hover": {
                        borderRadius: "4px",
                      },
                    },
                  }}
                >
                  {subMenu.map(
                    ({ label, to, icon, action, disabled }, index) => (
                      <React.Fragment key={label}>
                        <MenuItem
                          disabled={disabled}
                          onClick={() => {
                            if (!disabled) {
                              if (action === "logout") {
                                handleLogout();
                              } else if (action === "feedback") {
                                handleFeedback();
                              } else if (to) {
                                handleMenuItemClick(to);
                              }
                            }
                          }}
                        >
                          <img
                            src={icon}
                            alt={label}
                            style={{
                              width: "16px",
                              height: action === "logout" ? "14px" : "16px",
                              paddingRight: "8px",
                            }}
                          />
                          <Typography sx={{ fontSize: "14px" }}>
                            {label}
                          </Typography>
                        </MenuItem>
                        {index < subMenu.length - 1 && (
                          <Divider sx={{ margin: "4px 0" }} />
                        )}
                      </React.Fragment>
                    )
                  )}
                </Menu>
              </Box>
            ) : (
              <NavLink
                key={label}
                to={`/${role}/${to}`}
                style={{ textDecoration: "none" }}
              >
                {({ isActive }) => (
                  <NavItem
                    sx={{
                      padding: "7px 13px",
                      borderRadius: "20px",
                      backgroundColor: isActive ? "#F97316" : "transparent",
                      color: isActive ? "#fff" : "#111827",
                      transition: "all 0.2s ease",
                      boxShadow: isActive
                        ? "0 4px 10px rgba(255, 106, 0, 0.25)"
                        : "none",

                      "&:hover": {
                        backgroundColor: isActive
                          ? colors.primaryButtonOrange
                          : "rgba(255, 106, 0, 0.08)",
                        textDecoration: "none",
                      },
                    }}
                  >
                    <img
                      src={icon}
                      alt={label}
                      style={{
                        ...IconStyle,
                        ...style,
                        filter: isActive ? "brightness(0) invert(1)" : "none", // makes icon white
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: isActive ? "#fff" : "#000",
                      }}
                    >
                      {label}
                    </Typography>
                  </NavItem>
                )}
              </NavLink>
            )
          )}
        </Box>
      </Toolbar>
    </NavContainer>
  );
};

export default SellerNavbar;
